/**
 * Single source of truth for review status display and chip colours.
 * Used by Dashboard, Reviews to Manage, Your team and their teams, My Reviews,
 * All Reviews, and Review Detail so chips stay consistent everywhere.
 */

export type ReviewStatusChipColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning';

const NUMERIC_LABELS: Record<number, string> = {
  0: 'Not Started',
  1: 'Not Started',
  2: 'Self Review In Progress',
  3: 'Self Review Complete',
  4: 'Pending Discussion',
  5: 'Discussion Acknowledged',
  6: 'Finalised',
  7: 'Under Dispute',
  8: 'Excluded',
  9: 'Not Submitted',
};

const STRING_LABELS: Record<string, string> = {
  notstarted: 'Not Started',
  selfreviewinprogress: 'Self Review In Progress',
  selfreviewcomplete: 'Self Review Complete',
  notsubmitted: 'Not Submitted',
  pendingdiscussion: 'Pending Discussion',
  discussionacknowledged: 'Discussion Acknowledged',
  underdispute: 'Under Dispute',
  finalised: 'Finalised',
  excluded: 'Excluded',
};

/** Human-readable label for a review status (string or numeric from API). */
export function formatReviewStatus(status: string | number): string {
  if (typeof status === 'number') {
    return NUMERIC_LABELS[status] ?? `Status ${status}`;
  }
  const key = String(status).toLowerCase().replace(/\s/g, '');
  if (STRING_LABELS[key]) return STRING_LABELS[key];
  return String(status)
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/** MUI Chip color for review status. Each status has a distinct colour. */
export function getReviewStatusColor(status: string | number): ReviewStatusChipColor {
  const s = typeof status === 'string' ? status.toLowerCase().replace(/\s/g, '') : String(status);
  switch (s) {
    case 'notstarted':
    case '0':
    case '1':
      return 'default';
    case 'selfreviewinprogress':
    case '2':
      return 'info';
    case 'selfreviewcomplete':
    case '3':
      return 'warning';
    case 'notsubmitted':
    case '9':
      return 'error';
    case 'pendingdiscussion':
    case '4':
      return 'primary';
    case 'discussionacknowledged':
    case '5':
      return 'secondary';
    case 'finalised':
    case '6':
      return 'success';
    case 'underdispute':
    case '7':
      return 'error';
    case 'excluded':
    case '8':
      return 'default';
    default:
      return 'default';
  }
}

/** Canonical filter key for status (e.g. for dropdowns and URL params). One key per display label. */
export function getReviewStatusFilterKey(status: string | number): string {
  const s = typeof status === 'string' ? status.toLowerCase().replace(/\s/g, '') : String(status);
  switch (s) {
    case '0':
    case '1':
    case 'notstarted':
      return 'notstarted';
    case '2':
    case 'selfreviewinprogress':
      return 'selfreviewinprogress';
    case '3':
    case 'selfreviewcomplete':
      return 'selfreviewcomplete';
    case '9':
    case 'notsubmitted':
      return 'notsubmitted';
    case '4':
    case 'pendingdiscussion':
      return 'pendingdiscussion';
    case '5':
    case 'discussionacknowledged':
      return 'discussionacknowledged';
    case '6':
    case 'finalised':
      return 'finalised';
    case '7':
    case 'underdispute':
      return 'underdispute';
    case '8':
    case 'excluded':
      return 'excluded';
    default:
      return s;
  }
}

/** Ordered list of (filterKey, display label) for status filter dropdowns. Matches chip labels. */
export const REVIEW_STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'notstarted', label: 'Not Started' },
  { value: 'selfreviewinprogress', label: 'Self Review In Progress' },
  { value: 'selfreviewcomplete', label: 'Self Review Complete' },
  { value: 'notsubmitted', label: 'Not Submitted' },
  { value: 'pendingdiscussion', label: 'Pending Discussion' },
  { value: 'discussionacknowledged', label: 'Discussion Acknowledged' },
  { value: 'finalised', label: 'Finalised' },
  { value: 'underdispute', label: 'Under Dispute' },
  { value: 'excluded', label: 'Excluded' },
];
